import { _ } from "../../polyglot/polyglot.js";
import { timesSolidIcon } from "../../elements/icons/index.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import button from "@foretagsplatsen/finsit-design-system/widget-js/elements/button";
import FileUploadButtonWidget from "../FileUploadButtonWidget.js";
import UploadedFile from "../../../models/UploadedFile.js";

/**
 * `FileUploadWidget` renders a `FileUploadButtonWidget` and the name
 * of the uploaded file next to it.
 *
 * The parameters are the same as for `FileUploadButtonWidget` and:
 *
 * @param {boolean} [wantsPreview] - Bool used to define whether the file
 * uploaded thumbnail should be rendered. It only works with image files.
 */
export default class FileUploadWidget extends Widget {
	constructor({
		cssClass,
		dataTestName,
		wantsPreview = false,
		buttonCssClass,
		onSuccess,
	} = {}) {
		super(...arguments);

		this._cssClass = cssClass || "";
		this._dataTestName = dataTestName || "";
		this._wantsPreview = wantsPreview;

		this._uploadedFile = undefined;

		const buttonSpec = {
			label: _("Upload"),
			...arguments[0],
			cssClass: buttonCssClass,
			// spec.dataTestName is already used by the receiver in `renderContentOn`.
			// We must avoid using the same dataTestName in two places for the same button.
			dataTestName: undefined,
			onSuccess: (...rest) => {
				if (onSuccess) {
					onSuccess(...rest);
				}

				this._readFile(...rest);
			},
		};

		this._fileUploadButton = new FileUploadButtonWidget(buttonSpec);
	}

	getAttachment() {
		return this._uploadedFile;
	}

	setAttachment(attachment) {
		this._uploadedFile = attachment;
		this.update();
	}

	renderContentOn(html) {
		html.div(
			{
				klass: ["file-upload", this._cssClass],
				"data-test": this._dataTestName,
			},
			this._renderButtonOn.bind(this),
			this._renderPreviewOn.bind(this),
			this._renderFilenameOn.bind(this),
			this._renderDeleteOn.bind(this),
		);
	}

	wantsPreview() {
		return (
			this._wantsPreview &&
			this._uploadedFile &&
			this._uploadedFile.getContent()
		);
	}

	_renderButtonOn(html) {
		html.span({ klass: "button" }, this._fileUploadButton);
	}

	_renderPreviewOn(html) {
		if (!this.wantsPreview()) {
			return;
		}

		const href = this._uploadedFile.asBase64Url();
		const download = this._uploadedFile.getFilename();

		html.a(
			{ href, download },
			html.img({ klass: "image-preview", src: href }),
		);
	}

	_renderFilenameOn(html) {
		if (!this._uploadedFile) {
			return;
		}

		html.span({ klass: "filename" }, this._uploadedFile.getFilename());
	}

	_renderDeleteOn(html) {
		if (!this._uploadedFile) {
			return;
		}

		html.render(
			button(
				{
					as: "span",
					onClick: this.setAttachment.bind(this, null),
				},
				timesSolidIcon,
			),
		);
	}

	/**
	 * Process upload result and refresh the receiver with the
	 * filename and eventually the file preview if it is an image.
	 *
	 * @param {File} file - The file selected by the user and sent to
	 * the server.
	 * @param {object|object[]} [data] - The result received from the
	 * server after the upload has successfully completed. When an
	 * array, the first element is usually a raw `UploadedFile`.
	 */
	async _readFile(file, data) {
		let uploadedFileSpec = {
			fileName: file.name,
			contentType: file.type,
		};

		// When the server sends an array back, consider the first
		// element of the array to contain information about the
		// uploaded file.
		if (Array.isArray(data)) {
			uploadedFileSpec = { ...uploadedFileSpec, ...data[0] };
		}

		if (this._wantsPreview) {
			uploadedFileSpec.fileContent = await this._readFileContent(file);
		}

		this._uploadedFile = UploadedFile.materialize(uploadedFileSpec);
		this.update();
	}

	/**
	 * Read `file` and resolve to its content.
	 *
	 * @param {File} file - The file selected by the user and sent to
	 * the server.
	 */
	_readFileContent(file) {
		return new Promise((resolve) => {
			const reader = new FileReader();

			reader.onload = (data) => resolve(data.target.result.split(",")[1]);

			reader.readAsDataURL(file);
		});
	}
}
